import _ from 'lodash';
import { AST } from '../../ast';
import { Ty } from '../../ty';
import { EraQlAst } from '../eraql-ast/ast';
import { withNullMeta } from '../lex/types';
import { exprToEraQLAst } from './expr-to-eraql-ast';

import { fromAssoc, fromCallOf, fromList, fromLit } from './helpers';
import { tyToEraQlAst } from './ty-to-eraql-ast';
import { RelMacroChildren } from '../../ast/base';

export const relToEraQlAst = (rel: AST.RelFR): EraQlAst => {
  switch (rel.t) {
    case 'table': {
      return fromCallOf(
        'From',
        [
          fromAssoc<EraQlAst>(
            {
              name: fromLit(rel.name),
              schema: fromLit(rel.schema),
              attributes: renderTypes(rel.attributes),
            },
            (val) => val
          ),
        ],
        'fc'
      );
    }
    case 'generate-series': {
      return fromCallOf(
        'GenerateSeries',
        [exprToEraQLAst(rel.start), exprToEraQLAst(rel.stop)],
        'fc'
      );
    }
    case 'information-schema': {
      return fromCallOf(
        'InformationSchema',
        [
          fromAssoc<EraQlAst>(
            {
              type: fromLit(rel.type),
              schemas: fromList(rel.schemas, fromLit),
            },
            (val) => val
          ),
        ],
        'fc'
      );
    }
    case 'file': {
      return fromCallOf('File', [rel.uri, renderTypes(rel.attributes)], 'fc');
    }
    case 'union': {
      return fromCallOf(
        rel.all ? 'UnionAll' : 'UnionDistinct',
        [relToEraQlAst(rel.sources.left), relToEraQlAst(rel.sources.right)],
        'pipe'
      );
    }
    case 'values': {
      return fromCallOf(
        'Values',
        [
          fromList(
            rel.values.map((val) =>
              exprToEraQLAst({ t: 'scalar', val, ty: Ty.s(rel.attributes) })
            ),
            (x) => x
          ),
        ],
        'fc'
      );
    }
    case 'aggregate':
      return renderAggregate(rel);
    case 'select':
      return renderSelect(rel);
    case 'join':
      return renderJoin(rel);
    case 'rel-var':
      return renderRelVar(rel);
    case 'macro-rel-case':
    case 'macro-apply-vars-to-rel':
    default:
      throw new Error(`TODO: ${rel.t}`);
  }
};

const renderRelVar = (rel: AST._RelVar<RelMacroChildren>): EraQlAst => {
  return fromCallOf(
    'RelVar',
    [
      fromAssoc(
        {
          name: fromLit(rel.name),
          scope: fromLit(rel.scope),
        },
        (x) => x
      ),
      renderTypes(rel.attributes),
      ...(rel.default !== null
        ? [fromAssoc({ default: relToEraQlAst(rel.default) }, (x) => x)]
        : []),
    ],
    'fc'
  );
};

const renderAggregate = (
  rel: AST._Aggregate<AST.RelMacroChildren>
): EraQlAst => {
  const isSummary = rel.groupedAttributes.length === 0;

  if (isSummary) {
    const ast: EraQlAst = fromCallOf(
      'Summary',
      [relToEraQlAst(rel.sources.from), renderStruct(rel.selection)],
      'pipe'
    );

    return ast;
  }

  const ast: EraQlAst = fromCallOf(
    'Aggregate',
    [
      relToEraQlAst(rel.sources.from),
      fromList(rel.groupedAttributes, (name) => ({ t: 'ident', name })),
      renderStruct(rel.selection),
    ],
    'pipe'
  );

  return ast;
};

const renderJoin = (rel: AST._Join<AST.RelMacroChildren>): EraQlAst => {
  const joinType: Record<AST.JoinType, string> = {
    left: 'LeftJoin',
    right: 'RightJoin',
    inner: 'InnerJoin',
  };
  const refs = { left: 'l', right: 'r' };

  const selection: Record<string, EraQlAst> = _.mapValues(
    rel.selection,
    (expr) => exprToEraQLAst(expr, { refs })
  );

  const selectionAst = fromAssoc<EraQlAst>(
    {
      on: exprToEraQLAst(rel.condition, { refs }),
      select: fromAssoc(selection, (x) => x),
    },
    (x) => x
  );

  const selectionClosure: EraQlAst = {
    t: 'closure',
    args: withNullMeta(
      [refs.left, refs.right].map((sym) => ({ sym: withNullMeta(sym) }))
    ),
    body: withNullMeta(selectionAst),
  };

  const ast: EraQlAst = fromCallOf(
    joinType[rel.how],
    [
      relToEraQlAst(rel.sources.left),
      relToEraQlAst(rel.sources.right),
      selectionClosure,
    ],
    'pipe'
  );

  return ast;
};

const renderSelect = (rel: AST._Select<AST.RelMacroChildren>): EraQlAst => {
  const child = relToEraQlAst(rel.sources.from);

  const selectOpts: [string, EraQlAst][] = [];

  if (rel.limit !== null) {
    selectOpts.push(['limit', fromLit(rel.limit)]);
  }

  if (rel.offset !== null) {
    selectOpts.push(['limit', fromLit(rel.offset)]);
  }

  if (rel.distinct === true) {
    selectOpts.push(['distinct', fromLit(rel.distinct)]);
  }

  if (rel.orderBys.length > 0) {
    const orderBys: EraQlAst = fromList<EraQlAst>(
      rel.orderBys.map(({ direction, expr }) =>
        fromCallOf(direction, [exprToEraQLAst(expr)], 'fc')
      ),
      (x) => x
    );
    selectOpts.push(['order_by', orderBys]);
  }

  // TODO: Fix `Where`
  // const isPlainSelectStar = Object.entries(rel.selection).every(
  //   ([name, expr]) => expr.t === 'attr' && expr.name === name
  // );

  // if (isPlainSelectStar && rel.condition !== null && selectOpts.length === 0) {
  //   const ast: EraQlAst = {
  //     t: 'call',
  //     op: fromSymbol('Where'),
  //     args: [withNullMeta(child), withNullMeta(exprToEraQLAst(rel.condition))],
  //     display: 'pipe',
  //   };
  //   return ast;
  // }

  if (rel.condition !== null) {
    selectOpts.push(['where', exprToEraQLAst(rel.condition)]);
  }

  const optsAst =
    selectOpts.length > 0
      ? fromAssoc(Object.fromEntries(selectOpts), (x) => x, {
          alphabetize: true,
        })
      : null;

  const ast: EraQlAst = fromCallOf(
    'Select',
    [
      child,
      fromAssoc(rel.selection, (x) => exprToEraQLAst(x), {
        alphabetize: true,
      }),
      ...(optsAst ? [optsAst] : []),
    ],
    'pipe'
  );

  return ast;
};

const renderStruct = (
  struct: Record<string, AST.ExprFR>,
  opts?: { refs: Record<string, string> }
): EraQlAst => fromAssoc(struct, (expr) => exprToEraQLAst(expr, opts));

const renderTypes = (
  types: Record<string, Ty.ExtendedAttributeType>
): EraQlAst =>
  fromAssoc(types, (ty) => tyToEraQlAst(ty), { alphabetize: true });
